import React from 'react';
import { Link } from 'react-router-dom';
import {
  List, X, Bell, Sun, Moon, SignOut, WarningCircle,
  CaretDown, Key,
} from '@phosphor-icons/react';

// Inline test-mode banner surfaced at the shell level (between the top bar
// and main content). Enumerates every integration currently in test mode.
function TestModeBanner({ integrationsInTestMode, showSettingsLink, onGoToSettings }) {
  if (!integrationsInTestMode || integrationsInTestMode.length === 0) return null;
  const message = integrationsInTestMode.length === 1
    ? `${integrationsInTestMode[0]} is in test mode. Notifications from this integration are being redirected to test recipients.`
    : `${integrationsInTestMode.length} integrations in test mode: ${integrationsInTestMode.join(', ')}. Notifications from these are being redirected to test recipients.`;
  return (
    <div className="flex flex-shrink-0 items-center justify-center gap-3 border-b border-status-pending-finance/30 bg-status-pending-finance/8 px-4 py-2 text-sm text-status-pending-finance">
      <WarningCircle size={16} className="flex-none" />
      <span>{message}</span>
      {showSettingsLink && (
        <button onClick={onGoToSettings} className="ml-2 font-medium underline hover:opacity-80">
          Go to Settings →
        </button>
      )}
    </div>
  );
}

// The sidebar shell — rendered inside desktop <aside> and mobile drawer alike.
function SidebarShell({
  user, visibleNav, activeNavPath, entityChip, pendingCount, onNavigate, onLogout,
}) {
  const initials = (user?.name || '?').trim().split(/\s+/).map((s) => s[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div className="flex h-full flex-col">
      {/* Brand */}
      <div className="border-b border-paper-border px-6 py-5">
        <div className="font-serif text-xl text-ink">Aralsane</div>
        <div className="mt-0.5 text-xs text-ink-faint">Ticketing &amp; AI Customer Service</div>
      </div>

      {/* User info */}
      <div className="border-b border-paper-border px-4 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-paper-muted text-sm font-semibold text-ink">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-ink" dir="auto">{user?.name}</div>
            <div className="truncate text-xs capitalize text-ink-muted">{user?.role?.replace('_', ' ')}</div>
          </div>
        </div>
        {entityChip && <div className="mt-2">{entityChip}</div>}
      </div>

      {/* Nav */}
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {visibleNav.map((item) => {
          const Icon = item.icon;
          const active = item.path === activeNavPath;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onNavigate}
              className={`relative flex items-center gap-3 rounded-field px-3 py-2.5 text-sm transition-colors ${
                active
                  ? 'bg-status-under-process/8 font-medium text-status-under-process'
                  : 'text-ink-muted hover:bg-paper-muted hover:text-ink'
              }`}>
              {active && (
                <span className="absolute inset-y-1 left-0 w-0.5 rounded-full bg-status-under-process" />
              )}
              <Icon size={18} weight={active ? 'fill' : 'regular'} className="flex-none" />
              <span>{item.label}</span>
              {item.badge === 'pendingAction' && pendingCount > 0 && (
                <span className="ml-auto min-w-[22px] rounded-chip bg-status-pending-finance/12 px-2 py-0.5 text-center text-xs font-bold text-status-pending-finance">
                  {pendingCount}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="border-t border-paper-border px-3 py-4">
        <button
          onClick={onLogout}
          className="flex w-full items-center gap-3 rounded-field px-3 py-2.5 text-sm text-ink-muted transition-colors hover:bg-status-escalated/8 hover:text-status-escalated">
          <SignOut size={18} />
          Sign Out
        </button>
      </div>
    </div>
  );
}

export default function LayoutView({
  user,
  visibleNav,
  activeNavPath,
  pendingCount,
  entityChip,
  integrationsInTestMode,
  showBannerSettingsLink,
  darkMode,
  sidebarOpen,
  onOpenSidebar,
  onCloseSidebar,
  onNavigate,
  onLogout,
  onToggleDarkMode,
  onGoToSettings,
  avatarMenuOpen,
  avatarMenuRef,
  onToggleAvatarMenu,
  onChangePassword,
  children,
}) {
  return (
    <div className="flex h-screen bg-paper">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 flex-shrink-0 flex-col border-r border-paper-border bg-paper-panel lg:flex">
        <SidebarShell
          user={user}
          visibleNav={visibleNav}
          activeNavPath={activeNavPath}
          entityChip={entityChip}
          pendingCount={pendingCount}
          onNavigate={onNavigate}
          onLogout={onLogout}
        />
      </aside>

      {/* Mobile drawer overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm" onClick={onCloseSidebar} />
          <aside className="fixed left-0 top-0 z-50 flex h-full w-64 flex-col border-r border-paper-border bg-paper-panel shadow-soft">
            <div className="flex justify-end p-3">
              <button onClick={onCloseSidebar}
                className="inline-flex h-8 w-8 items-center justify-center rounded-field text-ink-muted hover:bg-paper-muted">
                <X size={18} />
              </button>
            </div>
            <SidebarShell
              user={user}
              visibleNav={visibleNav}
              activeNavPath={activeNavPath}
              entityChip={entityChip}
              pendingCount={pendingCount}
              onNavigate={onNavigate}
              onLogout={onLogout}
            />
          </aside>
        </div>
      )}

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Top bar */}
        <header className="flex flex-shrink-0 items-center justify-between border-b border-paper-border bg-paper-panel px-4 py-3 lg:px-6">
          <button
            onClick={onOpenSidebar}
            className="inline-flex h-8 w-8 items-center justify-center rounded-field text-ink-muted hover:bg-paper-muted lg:hidden">
            <List size={18} />
          </button>
          <div className="ml-2 flex items-center gap-2 lg:ml-0">
            {entityChip}
          </div>
          <div className="flex items-center gap-2">
            {/* Dark-mode toggle */}
            <button
              onClick={onToggleDarkMode}
              title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              className="inline-flex h-8 w-8 items-center justify-center rounded-field border border-paper-border2 text-ink-muted hover:bg-paper-muted">
              {darkMode
                ? <Sun size={14} weight="fill" className="text-status-pending-finance" />
                : <Moon size={14} />}
            </button>

            {/* Bell (decorative — preserved from live) */}
            <button className="inline-flex h-8 w-8 items-center justify-center rounded-field text-ink-muted hover:bg-paper-muted">
              <Bell size={16} />
            </button>

            {/* Avatar dropdown — Change password / Sign out */}
            <div className="relative" ref={avatarMenuRef}>
              <button
                onClick={onToggleAvatarMenu}
                className="flex items-center gap-1 rounded-full pr-1 transition hover:bg-paper-muted"
                title="Account menu">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-paper-muted text-sm font-semibold text-ink">
                  {(user?.name || '?').trim().charAt(0).toUpperCase()}
                </span>
                <CaretDown size={12} className="text-ink-faint" />
              </button>
              {avatarMenuOpen && (
                <div className="absolute right-0 top-full z-30 mt-2 w-48 overflow-hidden rounded-card border border-paper-border bg-paper-panel shadow-soft">
                  <button
                    onClick={onChangePassword}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-ink transition hover:bg-paper-muted">
                    <Key size={14} />Change password
                  </button>
                  <button
                    onClick={onLogout}
                    className="flex w-full items-center gap-2 border-t border-paper-border px-3 py-2 text-sm text-status-escalated transition hover:bg-status-escalated/8">
                    <SignOut size={14} />Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Test-mode banner */}
        <TestModeBanner
          integrationsInTestMode={integrationsInTestMode}
          showSettingsLink={showBannerSettingsLink}
          onGoToSettings={onGoToSettings}
        />

        {/* Page content — preserves the p-4 lg:p-6 pattern per approved spec */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
